import React, { useState, createContext, useContext } from 'react';
import * as yup from 'yup';
import { AlertContext } from 'Providers/Alert';
import { ApiContext } from 'components/ApiProvider';
import { AcknowlegementOptions, FrequencyOptions } from 'types/grants';
import { roundToCents } from 'util/money';
import moment from 'moment';
import { displayDate, getDateWithUtcOffset } from 'util/dates';

export const GrantsContext = createContext();
export const grantRequestValidationOptions = {
  grantAmountMinValue: 100,
  invalidGrantAmountMessage: 'The grant request minimum is $100 per grant request',
};

const schema = yup
  .array()
  .min(1, { name: 'grantee', message: 'You must choose at least one grantee' })
  .required()
  .of(
    yup.object().shape({
      granteeId: yup.mixed().required('Grantee ID is required'),
      fundId: yup.number().required('Fund ID is required'),
      totalAmount: yup
        .number()
        .when('$step', (step, s) => {
          return step > 1
            ? s
                .required({ name: 'amount', message: 'Amount is required' })
                .min(grantRequestValidationOptions.grantAmountMinValue, {
                  name: 'amount',
                  message: grantRequestValidationOptions.invalidGrantAmountMessage,
                })
            : s;
        })
        .when('$remainingFunds', (remainingFunds, s) => {
          return remainingFunds < 0
            ? s.oneOf(['error'], { name: 'amount', message: 'Not enough funds for grant amount' })
            : s;
        }),
      designation: yup
        .string()
        .when('$step', (step, s) => {
          return step > 2 ? s.required('Designation is required') : s;
        })
        .test('customInput', { name: 'designation', message: 'Required field' }, (value) => {
          return (
            ['In support of a specific project', 'In memory of', 'In honor of', 'Other'].filter(
              (a) => value === a
            ).length === 0
          );
        }),
      acknowledgement: yup
        .string()
        .when('$step', (step, s) => {
          return step > 2 ? s.required('Acknowledgement is required') : s;
        })
        .test('customInput', { name: 'acknowledgement', message: 'Required field' }, (value) => {
          return value !== 'Recognize Someone Else';
        }),
      timing: yup
        .string()
        .when('$step', (step, s) => {
          return step > 2 ? s.required('Timing is required') : s;
        })
        .test('futureDate', { name: 'timing', message: 'Required field' }, (value) => {
          return value !== 'On a specific future date:';
        })
        .test(
          'futureDate',
          {
            name: 'timing',
            message: 'Please enter a valid date or select a date from the calendar picker',
          },
          (value) => {
            const dateString = value.replace(/On a specific future date: /g, '');
            const momentDate = moment(dateString);

            return (
              !momentDate.isValid() ||
              (moment(displayDate(dateString)) >= moment(displayDate(new Date())) &&
                momentDate.year() <= 9999)
            );
          }
        )
        .test(
          'futureDate',
          {
            name: 'timing',
            message: `The closest available date for selection is ${displayDate(
              moment(displayDate(new Date())).add(4, 'days')
            )}`,
          },
          (value) => {
            const dateString = value.replace(/On a specific future date: /g, '');
            const momentDate = moment(dateString);

            return (
              !momentDate.isValid() || momentDate >= moment(displayDate(new Date())).add(4, 'days')
            );
          }
        ),
      recurring: yup.boolean().default(false),
      startDate: yup.date().when('recurring', {
        is: (recurring) => recurring,
        then: yup
          .date()
          .required({ name: 'startDate', message: 'Required field' })
          .test(
            'startDate',
            {
              name: 'startDate',
              message: 'Please enter a valid date or select a date from the calendar picker',
            },
            (value) => {
              const momentDate = moment(value);

              return (
                moment(displayDate(momentDate)) >= moment(displayDate(new Date())) &&
                momentDate.year() <= 9999
              );
            }
          )
          .test(
            'startDate',
            {
              name: 'startDate',
              message: `The closest available date for selection is ${displayDate(
                moment(displayDate(new Date())).add(4, 'days')
              )}`,
            },
            (value) => {
              const momentDate = moment(value);

              return momentDate >= moment(displayDate(new Date())).add(4, 'days');
            }
          ),
      }),
      frequency: yup.string().when('recurring', {
        is: true,
        then: yup
          .string()
          .required({ name: 'frequency', message: 'Frequency is required' })
          .oneOf(['Monthly', 'Quarterly', 'Yearly']),
      }),
      endDate: yup.date().notRequired(),
      allocations: yup
        .array()
        .of(
          yup.object().shape({
            poolId: yup.number().required(),
            amount: yup.number().required(),
          })
        )
        .when('$step', (step, s) => {
          return step > 3
            ? s
                .min(1, { name: 'allocation', message: 'Allocation is required' })
                .when('$allocationEqualsGrants', (allocationEqualsGrants, s2) => {
                  return !allocationEqualsGrants
                    ? s2.oneOf(['error'], {
                        name: 'allocation',
                        message: 'Allocations must equal total grants value',
                      })
                    : s2;
                })
            : s;
        })
        .when('$step', (step, s) => {
          return step > 3
            ? s.when('$notEnoughFundsPoolIds', (notEnoughFundsPoolIds, s2) => {
                return notEnoughFundsPoolIds.length > 0
                  ? s2.oneOf(['error'], {
                      name: 'allocation',
                      message: 'Not enough funds in the pool',
                      poolIds: notEnoughFundsPoolIds,
                    })
                  : s2;
              })
            : s;
        }),
    })
  );

export const GrantsProvider = ({ fund, children }) => {
  const [grants, setGrants] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [savedGrant, setSavedGrant] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const { removeAlert } = useContext(AlertContext);
  const { createGrant } = useContext(ApiContext);

  const updateGrant = (granteeId, key, value) => {
    const grant = grants.find((g) => g.granteeId === granteeId);

    if (value) {
      grant[key] = value;
    } else {
      delete grant[key];
    }

    setGrants([...grants]);
  };

  const updateSavedGrant = (grant) => {
    setSavedGrant({ ...grant });
  };

  const updateGrantsBySavedGrant = () => {
    const newGrants = grants.map((obj) =>
      obj.granteeId === savedGrant.granteeId ? savedGrant : obj
    );
    setGrants(newGrants);
  };

  const grantsTotal = () => {
    return grants.reduce((acc, grant) => acc + Number(grant.totalAmount), 0);
  };

  const removeGrant = (granteeId) => {
    const newArr = grants.filter((g) => g.granteeId !== granteeId);
    setGrants(newArr);
  };

  const clearValidationErrors = () => {
    setValidationErrors([]);
  };

  const isValid = async (step) => {
    const remainingFunds = fund.totalValue - grantsTotal() - fund.pendingGrantAmount;
    const fundPools = fund.poolFundUnits;
    let allocationEqualsGrants = true;
    let totalAllocatedAmount = 0;
    const notEnoughFundsPoolIds = [];

    // check to see if user has allocated more to the pools than there are funds
    // in them
    fundPools.forEach((pool) => {
      const poolAllocValue = grants
        .map((g) => g.allocations)
        .map((allocArray) => allocArray.find((a) => a.poolId === pool.poolId))
        .reduce((acc, p) => roundToCents(acc + Number(p?.amount || 0)), 0);

      totalAllocatedAmount += poolAllocValue;

      if (
        poolAllocValue &&
        poolAllocValue > roundToCents(pool.poolFundValue - pool.pendingAmount)
      ) {
        notEnoughFundsPoolIds.push(pool.poolId);
      }
    });

    if (roundToCents(totalAllocatedAmount) !== roundToCents(grantsTotal())) {
      allocationEqualsGrants = false;
    }

    try {
      const data = await schema.validate(grants, {
        context: {
          step,
          remainingFunds: roundToCents(remainingFunds),
          allocationEqualsGrants,
          notEnoughFundsPoolIds,
        },
        abortEarly: false,
      });

      removeAlert();
      setErrorMessage(null);
      clearValidationErrors();

      return data;
    } catch (e) {
      console.error(e.errors);
      setValidationErrors(e.errors);
      setErrorMessage(e.errors[0]);
      return false;
    }
  };

  const addGrant = (
    granteeId,
    granteeName,
    newGrantee,
    acknowledgment,
    designation,
    ein,
    address1,
    city,
    state,
    zip
  ) => {
    const grant = {
      fundId: fund.id,
      designation: designation == null ? 'General operations' : designation,
      acknowledgement: acknowledgment == null ? 'Anonymous' : acknowledgment,
      timing: 'As soon as possible',
      allocations: [],
      requestWireTransfer: false,
      designationCustomInput: '',
      acknowledgementCustomInput: '',
      timingCustomInput: '',
      comments: '',
      granteeId,
      granteeName,
      newGrantee,
      ein,
      address1,
      city,
      state,
      zip,
    };

    const existing = grants.find((g) => g.granteeId === granteeId);

    if (!existing) {
      setGrants([...grants, grant]);
    }
  };

  const submit = async () => {
    const errors = [];

    // can't make this async because every request grants a new auth token and
    // invalidates old token, thereby making subsequent requests fail
    for (let i = 0; i < grants.length; i += 1) {
      const grant = { ...grants[i] };

      const newGrant = grant;
      newGrant.acknowledgement = {
        key: AcknowlegementOptions.find((f) => grant.acknowledgement?.includes(f.label))?.value,
        value: grant.acknowledgement,
      };
      if (newGrant.frequency) {
        newGrant.frequency = {
          key: FrequencyOptions.find((f) => grant.frequency?.includes(f.label))?.value,
          value: grant.frequency,
        };
      }
      // for new grantees, must remove auto-generated granteeId for server
      if (grant.newGrantee) {
        delete grant.granteeId;
      }

      try {
        /* eslint-disable no-await-in-loop */
        if (newGrant.startDate) {
          newGrant.startDate = getDateWithUtcOffset(newGrant.startDate);
        }

        if (newGrant.endDate) {
          newGrant.endDate = getDateWithUtcOffset(newGrant.endDate);
        }

        await createGrant(newGrant);
      } catch (e) {
        errors.push({ granteeName: grant.granteeName });
      }
    }
    // await Promise.all(
    //   grants.map(async (grant) => {
    //     try {
    //       await createGrant(grant);

    //   })
    // );

    if (errors.length === 0) {
      return true;
    }

    return false;
  };

  return (
    <GrantsContext.Provider
      value={{
        grants,
        updateGrant,
        removeGrant,
        addGrant,
        isValid,
        submit,
        grantsTotal,
        updateSavedGrant,
        updateGrantsBySavedGrant,
        errorMessage,
        validationErrors,
        clearValidationErrors,
      }}
    >
      {children}
    </GrantsContext.Provider>
  );
};
